import React from 'react';
import { StandardTableActions, Badge, ColoredBadge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { Appointment } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import type { ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export const renderAppointmentStatusBadge = (status?: string, color?: string) => (
  status ? <ColoredBadge label={status} color={color} /> : null
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Appointments',
    description: 'Manage your appointments database',
    permissionModuleKey: 'appointments',
    route: {
      list: '/appointments',
      new: '/appointments/new',
      detail: (id: number) => `/appointments/${id}`,
      edit: (id: number) => `/appointments/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients-appointments',
      detail: (id: number) => `/crm-clients-appointments/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search appointments by name, client, or notes...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientAppName', 'clientId', 'assignedTo', 'clientAppStatus', 'appIsPrivate'
      ],
    },
    {
      name: 'schedule',
      label: 'Schedule',
      icon: '📅',
      fields: [
        'clientAppScheduleDateTime', 'clientAppFinishDateTime', 'clientAppDurationMins'
      ],
    },
    {
      name: 'details',
      label: 'Details',
      icon: '📋',
      fields: [
        'clientAppNotes', 'clientAppOutcome', 'clientAppCreatedAt', 'clientAppUpdatedAt'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientAppName',
        label: 'Appointment Name',
        placeholder: 'All Appointment Name',
        getValue: (item: Appointment) => item.clientAppName,
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: Appointment) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'assignedTo',
        label: 'Asgn',
        placeholder: 'All Users',
        type: 'select' as const,
        getValue: (item: Appointment) => 
          item.assignedToUser
            ? (item.assignedToUser.userDisplayName || `${item.assignedToUser.userFirstName || ''}`.trim() || `User #${item.assignedToUser.id}`)
            : (item.assignedTo ? `User #${item.assignedTo}` : ''),
        options: [],
      },
      {
        key: 'clientAppStatus',
        label: 'Status',
        placeholder: 'All Statuses',
        type: 'select' as const,
        getValue: (item: Appointment) => 
          item.clientAppStatusDescr?.as_descr || (item.clientAppStatus ? `Status #${item.clientAppStatus}` : ''),
        options: [],
      },
      {
        key: 'appIsPrivate',
        label: 'Private',
        placeholder: 'All Private',
        type: 'boolean' as const,
        getValue: (item: Appointment) => item.appIsPrivate ? 'Yes' : 'No',
        options: ['Yes', 'No'],
      },
      {
        key: 'clientAppScheduleDateTime',
        label: 'Schedule Date',
        placeholder: 'All Schedule Date',
        type: 'date' as const,
        getValue: (item: Appointment) =>
          item.clientAppScheduleDateTime
            ? formatDateGlobal(item.clientAppScheduleDateTime)
            : undefined,
        options: [],
      },
      {
        key: 'clientAppFinishDateTime',
        label: 'Finish Date',
        placeholder: 'All Finish Date',
        type: 'date' as const,
        getValue: (item: Appointment) =>
          item.clientAppFinishDateTime
            ? formatDateGlobal(item.clientAppFinishDateTime)
            : undefined,
        options: [],
      },
      {
        key: 'clientAppDurationMins',
        label: 'Duration (Minutes)',
        placeholder: 'All Duration',
        type: 'number' as const,
        getValue: (item: Appointment) => item.clientAppDurationMins?.toString(),
        options: [],
      },
      {
        key: 'clientAppNotes',
        label: 'Notes',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Appointment) => item.clientAppNotes,
        options: [],
      },
      {
        key: 'clientAppOutcome',
        label: 'Outcome',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Appointment) => item.clientAppOutcome,
        options: [],
      },
      {
        key: 'clientAppCreatedAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Appointment) =>
          item.clientAppCreatedAt
            ? formatDateGlobal(item.clientAppCreatedAt)
            : undefined,
        options: [],
      },
      {
        key: 'clientAppUpdatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: Appointment) =>
          item.clientAppUpdatedAt
            ? formatDateGlobal(item.clientAppUpdatedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Appointment[],
      filters: {
        clientAppName: string[] | string;
        clientId: string[];
        assignedTo: string[];
        clientAppStatus: string[];
        appIsPrivate: string[];
        clientAppScheduleDateTime: string[];
        clientAppFinishDateTime: string[];
        clientAppDurationMins: string[];
        clientAppNotes: string[] | string;
        clientAppOutcome: string[] | string;
        clientAppCreatedAt: string[];
        clientAppUpdatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Appointment Name filter
        const nameFilter = filters.clientAppName;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.clientAppName || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.clientAppName || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client filter (matches by display name or ID)
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Asgn filter (matches by display name or ID)
        if (filters.assignedTo && filters.assignedTo.length > 0) {
          const displayValue = item.assignedToUser
            ? (item.assignedToUser.userDisplayName || `${item.assignedToUser.userFirstName || ''}`.trim() || `User #${item.assignedToUser.id}`)
            : (item.assignedTo ? `User #${item.assignedTo}` : '');
          const matches = filters.assignedTo.some((filter) => {
            return displayValue === filter ||
                   (item.assignedTo && filter === `User #${item.assignedTo}`) ||
                   (item.assignedToUser?.userDisplayName && filter === item.assignedToUser.userDisplayName) ||
                   (item.assignedToUser?.id && filter === `User #${item.assignedToUser.id}`);
          });
          if (!matches) return false;
        }

        // Status filter (matches by description or ID)
        if (filters.clientAppStatus && filters.clientAppStatus.length > 0) {
          const displayValue = item.clientAppStatusDescr?.as_descr || (item.clientAppStatus ? `Status #${item.clientAppStatus}` : '');
          const matches = filters.clientAppStatus.some((filter) => {
            return displayValue === filter ||
                   (item.clientAppStatus && filter === `Status #${item.clientAppStatus}`) ||
                   (item.clientAppStatusDescr?.as_descr && filter === item.clientAppStatusDescr.as_descr);
          });
          if (!matches) return false;
        }

        // Private filter
        if (filters.appIsPrivate && filters.appIsPrivate.length > 0) {
          const isPrivate = item.appIsPrivate ? 'Yes' : 'No';
          if (!filters.appIsPrivate.includes(isPrivate)) return false;
        }

        // Schedule Date filter
        if (filters.clientAppScheduleDateTime.length > 0) {
          if (ranges.clientAppScheduleDateTime) {
            const range = ranges.clientAppScheduleDateTime;
            if (range.start && range.end && item.clientAppScheduleDateTime) {
              const itemDate = new Date(item.clientAppScheduleDateTime);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientAppScheduleDateTime
              ? formatDateGlobal(item.clientAppScheduleDateTime)
              : '';
            const matches = filters.clientAppScheduleDateTime.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Finish Date filter
        if (filters.clientAppFinishDateTime.length > 0) {
          if (ranges.clientAppFinishDateTime) {
            const range = ranges.clientAppFinishDateTime;
            if (range.start && range.end && item.clientAppFinishDateTime) {
              const itemDate = new Date(item.clientAppFinishDateTime);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientAppFinishDateTime
              ? formatDateGlobal(item.clientAppFinishDateTime)
              : '';
            const matches = filters.clientAppFinishDateTime.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Duration filter
        if (filters.clientAppDurationMins && filters.clientAppDurationMins.length > 0) {
          const value = item.clientAppDurationMins?.toString() || '';
          const matches = filters.clientAppDurationMins.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Notes filter
        const notesFilter = filters.clientAppNotes;
        if (typeof notesFilter === 'string' && notesFilter.trim() !== '') {
          const value = (item.clientAppNotes || '').toLowerCase();
          if (!value.includes(notesFilter.toLowerCase())) return false;
        } else if (Array.isArray(notesFilter) && notesFilter.length > 0) {
          const value = (item.clientAppNotes || '').toLowerCase();
          const matches = notesFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Outcome filter
        const outcomeFilter = filters.clientAppOutcome;
        if (typeof outcomeFilter === 'string' && outcomeFilter.trim() !== '') {
          const value = (item.clientAppOutcome || '').toLowerCase();
          if (!value.includes(outcomeFilter.toLowerCase())) return false;
        } else if (Array.isArray(outcomeFilter) && outcomeFilter.length > 0) {
          const value = (item.clientAppOutcome || '').toLowerCase();
          const matches = outcomeFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Created At filter
        if (filters.clientAppCreatedAt.length > 0) {
          if (ranges.clientAppCreatedAt) {
            const range = ranges.clientAppCreatedAt;
            if (range.start && range.end && item.clientAppCreatedAt) {
              const itemDate = new Date(item.clientAppCreatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientAppCreatedAt
              ? formatDateGlobal(item.clientAppCreatedAt)
              : '';
            const matches = filters.clientAppCreatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Updated At filter
        if (filters.clientAppUpdatedAt.length > 0) {
          if (ranges.clientAppUpdatedAt) {
            const range = ranges.clientAppUpdatedAt;
            if (range.start && range.end && item.clientAppUpdatedAt) {
              const itemDate = new Date(item.clientAppUpdatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientAppUpdatedAt
              ? formatDateGlobal(item.clientAppUpdatedAt)
              : '';
            const matches = filters.clientAppUpdatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    customButton: {
      visible: true,
      icon: 'calendar',
      title: 'View Calendar',
      href: '/appointments_calendar_view',
      variant: 'default' as const,
    },
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Appointment',
        showTitle: false,
        variant: 'primary' as const,
        href: '/appointments/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Appointment) => {
      window.location.href = `/appointments/${item.clientAppId}`;
    },
    keyExtractor: (item: Appointment) => item.clientAppId,
    getClientId: (item: Appointment) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new appointment.',
    noSearchResults: 'No appointments found matching your search criteria.',
    noFilterResults: 'No appointments match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Appointment',
    message: 'Are you sure you want to delete this appointment? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete appointment. Please try again.',
    handler: async (id: number) => {
      const { deleteAppointment } = await import('../../services/mutations');
      await deleteAppointment(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Appointment Details',
    notFoundTitle: 'Appointment not found',
    notFoundMessage: 'The appointment you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Appointments',
    defaultTitle: 'Unnamed Appointment',
    getTitle: (item: Appointment) => {
      return item.clientAppName || `Appointment #${item.clientAppId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Appointment',
      subtitle: 'Add a new appointment to your database',
      submitText: 'Create Appointment',
    },
    edit: {
      title: 'Edit Appointment',
      submitText: 'Update Appointment',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading appointment...',
    errorMessage: 'Failed to save appointment. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<Appointment>, files?: File[], filesToDetach?: number[]) => {
      const { createAppointment } = await import('../../services/mutations');
      return await createAppointment(data, files);
    },
    update: async (id: number, data: Partial<Appointment>, files?: File[], filesToDetach?: number[]) => {
      const { updateAppointment } = await import('../../services/mutations');
      return await updateAppointment(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/crm-clients-appointments/appointment/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientAppName',
      label: 'Appointment Name',
      type: 'text',
      required: true,
      placeholder: 'Enter appointment name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
    },
    {
      name: 'clientAppScheduleDateTime',
      label: 'Schedule Date & Time',
      type: 'datetime',
      required: true,
      placeholder: 'Select schedule date and time',
      validation: {
        required: true,
      },
      // Allow calculating schedule/start from finish − duration (using duration select)
      calculateStartFrom: {
        endDateTimeField: 'clientAppFinishDateTime',
        durationMinutesField: 'clientAppDuration',
      },
    },
    {
      name: 'clientAppDuration',
      label: 'Duration',
      type: 'select',
      placeholder: 'Select duration (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'clientAppFinishDateTime',
      label: 'Finish Date & Time',
      type: 'datetime',
      placeholder: 'Select finish date and time',
      // Allow calculating finish/end from schedule/start + duration (using duration select)
      calculateEndFrom: {
        startDateTimeField: 'clientAppScheduleDateTime',
        durationMinutesField: 'clientAppDuration',
      },
    },
    {
      name: 'assignedTo',
      label: 'Asgn',
      type: 'select',
      required: true,
      placeholder: 'Select user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'clientAppNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter appointment notes',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'clientAppOutcome',
      label: 'Outcome',
      type: 'text',
      placeholder: 'Enter appointment outcome',
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'clientAppStatus',
      label: 'Status',
      type: 'select',
      required: true,
      placeholder: 'Select status',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-app-status', // API endpoint to fetch all status options
      labelField: 'as_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'appIsPrivate',
      label: 'Private',
      type: 'switch',
      switchLabel: 'Mark as private',
      description: 'Check if this appointment is private',
      labelPosition: 'left',
      defaultValue: false,
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📅',
        gridCols: 'md:grid-cols-2',
        fields: ['clientAppName', 'clientId', 'assignedTo'],
        collapsible: true,
      },
      {
        id: 'schedule',
        title: 'Schedule',
        icon: '⏰',
        gridCols: 'md:grid-cols-2',
        fields: ['clientAppScheduleDateTime', 'clientAppDuration', 'clientAppDurationMins', 'clientAppFinishDateTime'],
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Details',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['clientAppNotes', 'clientAppOutcome', 'clientAppStatus', 'appIsPrivate'],
        fieldSpans: {
          clientAppNotes: 'md:col-span-12',
          clientAppOutcome: 'md:col-span-6',
          clientAppStatus: 'md:col-span-6',
          appIsPrivate: 'md:col-span-6',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string,
  _opts?: unknown,
  colors?: ThemeColors
): Column<Appointment>[] => [
  {
    key: 'clientAppName',
    header: 'Name',
    sortable: true,
    render: (value, row) => {
      const name = value ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : '-';
      const hasAttachments = (row.FileAttachment?.length ?? 0) > 0;
      const attachmentCount = row.FileAttachment?.length ?? 0;
      if (!hasAttachments) return name;
      return (
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <span className="truncate">{name}</span>
          <span
            title={`${attachmentCount} file${attachmentCount !== 1 ? 's' : ''} attached`}
            className="shrink-0 inline-flex text-gray-500 dark:text-gray-400"
            aria-hidden
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </span>
        </span>
      );
    },
  },
  {
    key: 'client.clientName',
    header: 'Client',
    sortable: true,
    render: (value, row) => {
      const clientName = row.client?.clientName || '-';
      if (clientName === '-') return clientName;

      const displayName =
        clientName.length > 50 ? clientName.substring(0, 50) + '...' : clientName;
      const email = row.client?.clientEmail?.trim();
      const phone = row.client?.clientPhone?.trim();

      return (
        <div className="min-w-0">
          <div>{displayName}</div>
          {(email || phone) && (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              {email && (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <svg
                    className="h-3 w-3 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="truncate">{email}</span>
                </span>
              )}
              {phone && (
                <span className="inline-flex items-center gap-1">
                  <svg
                    className="h-3 w-3 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.517l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span>{phone}</span>
                </span>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'assignedTo',
    header: 'Asgn',
    sortable: true,
    render: (value, row) => (
      row.assignedToUser
        ? (row.assignedToUser.userDisplayName || `${row.assignedToUser.userFirstName || ''}`.trim() || `User #${row.assignedToUser.id}`)
        : (row.assignedTo ? `User #${row.assignedTo}` : '-')
    ),
  },
  {
    key: 'clientAppScheduleDateTime',
    header: 'Schedule',
    sortable: true,
    render: (value) => formatDateTime(value),
  },
  {
    key: 'clientAppDurationMins',
    header: 'Duration',
    sortable: true,
    render: (value, row) => (
      row.clientAppDurationDescr?.pd_descr || (value ? `${value} min` : '-')
    ),
  },
  {
    key: 'clientAppStatus',
    header: 'Status',
    sortable: true,
    render: (value, row) => {
      const status = row.clientAppStatusDescr?.as_descr || (value ? String(value) : '');
      if (!status) return '-';
      return renderAppointmentStatusBadge(status, row.clientAppStatusDescr?.as_color);
    },
  },
  {
    key: 'appIsPrivate',
    header: 'Private',
    sortable: true,
    render: (value) => (
      <Badge variant={value ? 'danger' : 'secondary'}>
        {value ? 'Private' : 'Public'}
      </Badge>
    ),
  },
  {
    key: 'clientAppCreatedAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Appointment,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📅',
      fields: [
        {
          label: 'Appointment Name',
          value: item.clientAppName || '-',
        },
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : 'None'),
        },
        {
          label: 'Asgn',
          value: item.assignedToUser
            ? (item.assignedToUser.userDisplayName || `${item.assignedToUser.userFirstName || ''}`.trim() || `User #${item.assignedToUser.id}`)
            : (item.assignedTo ? `User #${item.assignedTo}` : 'Unassigned'),
        },
        {
          label: 'Status',
          value:
            renderAppointmentStatusBadge(
              item.clientAppStatusDescr?.as_descr || item.clientAppStatus?.toString() || '',
              item.clientAppStatusDescr?.as_color,
            ) || '-',
        },
        {
          label: 'Private',
          value: item.appIsPrivate ? 'Yes' : 'No',
        },
      ],
    },
    {
      title: 'Schedule',
      icon: '⏰',
      fields: [
        {
          label: 'Schedule Date & Time',
          value: formatDate(item.clientAppScheduleDateTime),
        },
        {
          label: 'Duration',
          value: item.clientAppDurationDescr
            ? item.clientAppDurationDescr.pd_descr
            : (item.clientAppDurationMins ? `${item.clientAppDurationMins} minutes` : '-'),
        },
        {
          label: 'Finish Date & Time',
          value: formatDate(item.clientAppFinishDateTime),
        },
      ],
    },
    {
      title: 'Details',
      icon: '📋',
      fields: [
        {
          label: 'Notes',
          value: item.clientAppNotes || '-',
        },
        {
          label: 'Outcome',
          value: item.clientAppOutcome || '-',
        },
        {
          label: 'Created At',
          value: formatDate(item.clientAppCreatedAt),
        },
        {
          label: 'Updated At',
          value: formatDate(item.clientAppUpdatedAt),
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `/crm-clients-appointments/appointment/${item.clientAppId}/files`,
            files: item.FileAttachment || [],
            defaultDownloadName: `appointment_${item.clientAppId}_files.zip`,
            getViewFileUrl: (fileId) => `/crm-clients-appointments/appointment/${item.clientAppId}/files/${fileId}`,
          }),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: Appointment, _colors?: ThemeColors) => {
  const status = item.clientAppStatusDescr?.as_descr || '';
  return (
    <>
      {item.appIsPrivate && (
        <Badge variant="danger">Private</Badge>
      )}
      {status && (
        renderAppointmentStatusBadge(status, item.clientAppStatusDescr?.as_color)
      )}
    </>
  );
};

// Helper function to get initial form data
export const getInitialFormData = (): Partial<Appointment> => {
  return {
    clientAppName: '',
    clientId: undefined,
    assignedTo: undefined,
    clientAppScheduleDateTime: undefined,
    clientAppDuration: undefined,
    clientAppDurationMins: undefined,
    clientAppFinishDateTime: undefined,
    clientAppNotes: '',
    clientAppOutcome: '',
    clientAppStatus: undefined,
    appIsPrivate: false,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Appointment): Partial<Appointment> => {
  return {
    clientAppName: item.clientAppName || '',
    clientId: item.clientId,
    assignedTo: item.assignedTo,
    clientAppScheduleDateTime: item.clientAppScheduleDateTime,
    clientAppDuration: item.clientAppDuration,
    clientAppDurationMins: item.clientAppDurationMins,
    clientAppFinishDateTime: item.clientAppFinishDateTime,
    clientAppNotes: item.clientAppNotes || '',
    clientAppOutcome: item.clientAppOutcome || '',
    clientAppStatus: item.clientAppStatus,
    appIsPrivate: item.appIsPrivate ?? false,
  };
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'calendar':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: Appointment,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.clientAppId)}
      editTo={moduleConfig.module.route.edit(item.clientAppId)}
      onDelete={() => onDelete(item.clientAppId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

export type ModuleConfig = typeof moduleConfig;

